"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState } from "react";
import { FaPhoneAlt } from "react-icons/fa";
import { HiCalendar, HiCreditCard, HiMiniChatBubbleLeftRight } from "react-icons/hi2";
import { MdDragIndicator } from "react-icons/md";
import CallControls from "@/components/call/call-controls";
import { CallScribeButton, CallScribeSection, useCallScribe } from "@/components/call/call-scribe";
import DialedDigitsDisplay from "@/components/call/dialed-digits-display";
import DtmfKeypad from "@/components/call/dtmf-keypad";
import TransferDialog from "@/components/call/transfer-dialog";
import { formatTime, useCallTimer } from "@/components/call/use-call-timer";
import { useDTMF } from "@/components/call/use-dtmf";
import PatientDisplay from "@/components/patient/patient-display";
import Avatar from "@/components/ui/avatar";
import { DraggableContainer } from "@/components/ui/draggable-container";
import { Skeleton } from "@/components/ui/skeleton";
import { useBeforeUnload } from "@/hooks/use-before-unload";
import { formatTime as formatAppointmentTime, formatCurrency, formatDate, formatPhoneNumber, formatWeekdayShort } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { useInboundCall } from "./inbound-call-provider";
import { useCallerEnrichment } from "./use-caller-enrichment";
import { patientSidebarDefinition } from "@/features/sidebar/patient-sidebar";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
const ActiveInboundCall: React.FC = () => {
  const $ = _c(98);
  const inbound = useInboundCall();
  const [showKeypad, setShowKeypad] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const {
    elapsedTime,
    startTimer,
    stopTimer,
    timerInterval
  } = useCallTimer();
  const t0 = inbound?.currentCall ?? null;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      call: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    dialedDigits,
    sendDTMF,
    resetDialedDigits
  } = useDTMF(t1);
  const openSidebar = useRightSidebarStore(_temp);
  const matchedPatient = inbound?.matchedPatient ?? null;
  const t2 = inbound?.activeCallId ?? null;
  const t3 = matchedPatient?.id ?? null;
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = {
      callId: t2,
      patientId: t3
    };
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const scribe = useCallScribe(t4);
  const {
    nextAppointment,
    isAppointmentsLoading,
    sidebarCounts,
    isSidebarCountsLoading,
    outstandingBalance,
    isChargesLoading
  } = useCallerEnrichment(matchedPatient?.id ?? null);
  useBeforeUnload(inbound?.callState === "active", "You are currently on a call. Are you sure you want to leave? Your call will be disconnected.");
  let t5;
  if ($[5] !== inbound?.callState || $[6] !== resetDialedDigits || $[7] !== startTimer || $[8] !== stopTimer || $[9] !== timerInterval.current) {
    t5 = () => {
      if (inbound?.callState === "active") {
        startTimer();
      } else {
        stopTimer();
        resetDialedDigits();
        setShowKeypad(false);
        setShowTransferDialog(false);
      }
      return () => {
        if (timerInterval.current) {
          clearInterval(timerInterval.current);
        }
      };
    };
    $[5] = inbound?.callState;
    $[6] = resetDialedDigits;
    $[7] = startTimer;
    $[8] = stopTimer;
    $[9] = timerInterval.current;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = inbound?.callState;
  let t7;
  if ($[11] !== t6) {
    t7 = [t6];
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  useEffect(t5, t7);
  if (!inbound || inbound.callState !== "active") {
    return null;
  }
  const callerName = inbound.callerInfo?.callerName && inbound.callerInfo.callerName !== "Unknown" ? inbound.callerInfo.callerName : null;
  let t8;
  if ($[13] !== inbound.callerInfo) {
    t8 = inbound.callerInfo?.callerNumber ? formatPhoneNumber(inbound.callerInfo.callerNumber) : "Unknown Number";
    $[13] = inbound.callerInfo;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const callerNumber = t8;
  const t9 = showKeypad ? "w-[390px]" : "w-[360px]";
  let t10;
  if ($[15] !== t9) {
    t10 = cx("overflow-hidden rounded-2xl border border-border bg-background/95 shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-xl transition-[width] duration-200", t9);
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="absolute right-2 top-2 cursor-grab text-foreground-muted hover:text-foreground-secondary"><MdDragIndicator size={20} /></div>;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = matchedPatient?.profileImageUrl;
  const t13 = matchedPatient ? `${matchedPatient.firstName} ${matchedPatient.lastName}` : callerName ?? undefined;
  let t14;
  if ($[18] !== t12 || $[19] !== t13) {
    t14 = <div className="flex-shrink-0"><div className="rounded-full ring-2 ring-status-success-text/30"><Avatar src={t12} fallback={t13} size="lg" /></div></div>;
    $[18] = t12;
    $[19] = t13;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== callerName || $[22] !== callerNumber || $[23] !== matchedPatient) {
    t15 = <div className="flex min-w-0 flex-1 flex-col">{matchedPatient ? <PatientDisplay patient={matchedPatient} showPhoneNumber={true} showDateOfBirth={true} showMemberships={true} layout="vertical" useLinks={true} nameClassName="text-h4 leading-tight" /> : <>{callerName && <h3 className="truncate text-h4 leading-tight text-foreground">{callerName}</h3>}<span className="text-caption tabular-nums text-foreground-secondary">{callerNumber}</span></>}</div>;
    $[21] = callerName;
    $[22] = callerNumber;
    $[23] = matchedPatient;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t14 || $[26] !== t15) {
    t16 = <div className="relative px-4 pb-3 pt-4">{t11}<div className="flex items-center gap-3">{t14}{t15}</div></div>;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-status-success-text opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-status-success-text" /></span>;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  const t18 = inbound.isTransferring ? "Transferring..." : "On Call";
  let t19;
  if ($[29] !== t18) {
    t19 = <div className="flex items-center gap-2">{t17}<span className="text-caption font-medium text-foreground-secondary">{t18}</span></div>;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <FaPhoneAlt className="h-2.5 w-2.5 text-foreground-muted" />;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== elapsedTime) {
    t21 = formatTime(elapsedTime);
    $[32] = elapsedTime;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== t21) {
    t22 = <div className="flex items-center gap-1.5">{t20}<span className="text-caption font-semibold tabular-nums text-foreground">{t21}</span></div>;
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t19 || $[37] !== t22) {
    t23 = <div className="flex items-center justify-between border-t border-border/60 bg-background-secondary/40 px-4 py-2">{t19}{t22}</div>;
    $[36] = t19;
    $[37] = t22;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  let t24;
  if ($[39] !== isAppointmentsLoading || $[40] !== isChargesLoading || $[41] !== isSidebarCountsLoading || $[42] !== matchedPatient || $[43] !== nextAppointment || $[44] !== openSidebar || $[45] !== outstandingBalance || $[46] !== sidebarCounts) {
    t24 = matchedPatient && <div className="space-y-2"><div className="space-y-1 rounded-lg border border-border/60 bg-background-secondary/30 px-3 py-2"><div className="flex items-center gap-2 py-0.5"><HiCreditCard className={cx("h-3.5 w-3.5 flex-shrink-0", outstandingBalance > 0 ? "text-status-error-text" : "text-foreground-muted")} /><span className="w-16 flex-shrink-0 text-caption text-foreground-muted">Balance</span>{isChargesLoading ? <Skeleton className="h-3.5 w-16" /> : outstandingBalance > 0 ? <span className="text-caption font-medium text-status-error-text">{formatCurrency(outstandingBalance)}</span> : <span className="text-caption text-foreground-muted">No balance</span>}</div><div className="flex items-center gap-2 py-0.5"><HiCalendar className={cx("h-3.5 w-3.5 flex-shrink-0", nextAppointment ? "text-accent-foreground" : "text-foreground-muted")} /><span className="w-16 flex-shrink-0 text-caption text-foreground-muted">Next Appt</span>{isAppointmentsLoading ? <Skeleton className="h-3.5 w-32" /> : nextAppointment ? <span className="text-caption text-foreground-secondary">{formatWeekdayShort(nextAppointment.start)},{" "}{formatDate(nextAppointment.start, true, false)} at{" "}{formatAppointmentTime(nextAppointment.start)}</span> : <span className="text-caption text-foreground-muted">None scheduled</span>}</div><div className="flex items-center gap-2 py-0.5"><HiMiniChatBubbleLeftRight className="h-3.5 w-3.5 flex-shrink-0 text-foreground-muted" /><span className="w-16 flex-shrink-0 text-caption text-foreground-muted">Memos</span>{isSidebarCountsLoading ? <Skeleton className="h-3.5 w-8" /> : sidebarCounts?.memosCount ? <span className="inline-flex items-center rounded-full bg-status-warning-bg px-1.5 py-0.5 text-tiny font-medium leading-none text-status-warning-text">{sidebarCounts.memosCount}</span> : <span className="text-caption text-foreground-muted">None</span>}</div></div><button type="button" onClick={() => openSidebar(patientSidebarDefinition, {
        patientTiny: matchedPatient
      })} className="w-full rounded-lg border border-border/60 bg-background-secondary/30 px-3 py-2 text-caption font-medium text-foreground-secondary transition-colors duration-150 hover:bg-hover hover:text-foreground">View Patient Details</button></div>;
    $[39] = isAppointmentsLoading;
    $[40] = isChargesLoading;
    $[41] = isSidebarCountsLoading;
    $[42] = matchedPatient;
    $[43] = nextAppointment;
    $[44] = openSidebar;
    $[45] = outstandingBalance;
    $[46] = sidebarCounts;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== callerNumber || $[49] !== matchedPatient) {
    t25 = !matchedPatient && <div className="rounded-lg border border-border/60 bg-background-secondary/30 px-3 py-2.5 text-center"><p className="text-caption text-foreground-muted">Unknown caller</p><p className="mt-0.5 text-caption text-foreground-secondary">{callerNumber}</p></div>;
    $[48] = callerNumber;
    $[49] = matchedPatient;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== scribe) {
    t26 = <CallScribeSection scribe={scribe} />;
    $[51] = scribe;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  const t27 = showKeypad ? "flex-row" : "flex-col";
  let t28;
  if ($[53] !== t27) {
    t28 = cx("flex gap-4", t27);
    $[53] = t27;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== showKeypad) {
    t29 = () => setShowKeypad(!showKeypad);
    $[55] = showKeypad;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== inbound.activeCallId) {
    t30 = inbound.activeCallId ? () => setShowTransferDialog(true) : undefined;
    $[57] = inbound.activeCallId;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] !== scribe) {
    t31 = <CallScribeButton scribe={scribe} />;
    $[59] = scribe;
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  let t32;
  if ($[61] !== inbound.hangup || $[62] !== inbound.isMuted || $[63] !== inbound.isTransferring || $[64] !== inbound.toggleMute || $[65] !== scribe.isSaving || $[66] !== showKeypad || $[67] !== t29 || $[68] !== t30 || $[69] !== t31) {
    t32 = <CallControls isMuted={inbound.isMuted} onToggleMute={inbound.toggleMute} onHangup={inbound.hangup} showKeypad={showKeypad} onToggleKeypad={t29} onTransfer={t30} transferDisabled={inbound.isTransferring} hangupDisabled={scribe.isSaving} scribeMenu={t31} />;
    $[61] = inbound.hangup;
    $[62] = inbound.isMuted;
    $[63] = inbound.isTransferring;
    $[64] = inbound.toggleMute;
    $[65] = scribe.isSaving;
    $[66] = showKeypad;
    $[67] = t29;
    $[68] = t30;
    $[69] = t31;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== dialedDigits) {
    t33 = <DialedDigitsDisplay digits={dialedDigits} />;
    $[71] = dialedDigits;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== t32 || $[74] !== t33) {
    t34 = <div className="flex flex-1 flex-col gap-2">{t32}{t33}</div>;
    $[73] = t32;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== sendDTMF || $[77] !== showKeypad) {
    t35 = showKeypad && <DtmfKeypad onSendDigits={sendDTMF} />;
    $[76] = sendDTMF;
    $[77] = showKeypad;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== t28 || $[80] !== t34 || $[81] !== t35) {
    t36 = <div className={t28}>{t34}{t35}</div>;
    $[79] = t28;
    $[80] = t34;
    $[81] = t35;
    $[82] = t36;
  } else {
    t36 = $[82];
  }
  let t37;
  if ($[83] !== t24 || $[84] !== t25 || $[85] !== t26 || $[86] !== t36) {
    t37 = <div className="space-y-3 p-4">{t24}{t25}{t26}{t36}</div>;
    $[83] = t24;
    $[84] = t25;
    $[85] = t26;
    $[86] = t36;
    $[87] = t37;
  } else {
    t37 = $[87];
  }
  let t38;
  if ($[88] !== inbound.activeCallId || $[89] !== inbound.onTransferInitiated || $[90] !== showTransferDialog) {
    t38 = inbound.activeCallId && <TransferDialog open={showTransferDialog} onOpenChange={setShowTransferDialog} callId={inbound.activeCallId} onTransferInitiated={inbound.onTransferInitiated} />;
    $[88] = inbound.activeCallId;
    $[89] = inbound.onTransferInitiated;
    $[90] = showTransferDialog;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  let t39;
  if ($[92] !== t10 || $[93] !== t16 || $[94] !== t23 || $[95] !== t37 || $[96] !== t38) {
    t39 = <DraggableContainer className={t10} defaultRight="20px" defaultTop="80px" zIndex={50}>{t16}{t23}{t37}{t38}</DraggableContainer>;
    $[92] = t10;
    $[93] = t16;
    $[94] = t23;
    $[95] = t37;
    $[96] = t38;
    $[97] = t39;
  } else {
    t39 = $[97];
  }
  return t39;
};
export default ActiveInboundCall;
function _temp(state) {
  return state.open;
}